import { useEffect, useRef, useState, useCallback } from 'react';

const MAX_RECORDING_MS = 30000; // 30-second hard cap

/**
 * Push-to-talk hook using either Control key.
 * Hold Ctrl to record, release to stop and send audio.
 *
 * Defensive guards:
 * - 30s max recording timeout (auto-stops regardless of key state)
 * - Visibility/blur guard (stops if tab hidden or window blurs)
 * - State reconciliation on keydown (resets if state desynced)
 * - Full cleanup on unmount
 *
 * @param {{ enabled: boolean, onRecordingComplete: (blob: Blob) => void }} options
 * @returns {{ isRecording: boolean, error: string|null }}
 */
const usePushToTalk = ({ enabled, onRecordingComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);

  const isKeyDownRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const pendingStopRef = useRef(false);
  const timeoutRef = useRef(null);
  const isRecordingRef = useRef(false); // Mirror of isRecording for event handlers

  // Store callback in ref to avoid dependency churn in the useEffect
  const onRecordingCompleteRef = useRef(onRecordingComplete);
  onRecordingCompleteRef.current = onRecordingComplete;

  // Keep isRecordingRef in sync with state
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const clearRecordingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const stopRecording = () => {
      clearRecordingTimeout();
      pendingStopRef.current = true;

      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop(); // triggers onstop → cleanup
      } else {
        // MediaRecorder already stopped or never started — force cleanup
        releaseStream();
        setIsRecording(false);
        mediaRecorderRef.current = null;
        chunksRef.current = [];
      }
    };

    const startRecording = async () => {
      setError(null);
      pendingStopRef.current = false;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // User released key while we were waiting for mic permission
        if (pendingStopRef.current) {
          stream.getTracks().forEach(track => track.stop());
          setIsRecording(false);
          pendingStopRef.current = false;
          return;
        }

        streamRef.current = stream;
        chunksRef.current = [];

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm'
        });

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
        };

        mediaRecorder.onstop = () => {
          clearRecordingTimeout();
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          chunksRef.current = [];
          mediaRecorderRef.current = null;

          releaseStream();
          setIsRecording(false);

          if (blob.size > 0 && onRecordingCompleteRef.current) {
            onRecordingCompleteRef.current(blob);
          }
        };

        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start(100);
        setIsRecording(true);

        // Start max recording timeout
        timeoutRef.current = setTimeout(() => {
          console.warn('Push-to-talk: max duration reached (30s), auto-stopping');
          isKeyDownRef.current = false;
          stopRecording();
        }, MAX_RECORDING_MS);

        // Check again — key may have been released during MediaRecorder setup
        if (pendingStopRef.current) {
          pendingStopRef.current = false;
          mediaRecorder.stop();
        }
      } catch (err) {
        setError(err.message || 'Microphone access denied');
        setIsRecording(false);
        pendingStopRef.current = false;
        clearRecordingTimeout();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key !== 'Control') return;
      if (isKeyDownRef.current) return; // Prevent key-repeat firing

      // State reconciliation: if isRecording but MediaRecorder is not active, force reset
      if (isRecordingRef.current && (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording')) {
        setIsRecording(false);
        releaseStream();
        mediaRecorderRef.current = null;
        chunksRef.current = [];
        clearRecordingTimeout();
      }

      isKeyDownRef.current = true;
      startRecording();
    };

    const handleKeyUp = (e) => {
      if (e.key !== 'Control') return;
      isKeyDownRef.current = false;
      stopRecording();
    };

    // Stop recording if user switches tabs or minimizes
    const handleVisibilityChange = () => {
      if (document.hidden && isRecordingRef.current) {
        isKeyDownRef.current = false;
        stopRecording();
      }
    };

    // Stop recording if window loses focus
    const handleWindowBlur = () => {
      if (isRecordingRef.current) {
        isKeyDownRef.current = false;
        stopRecording();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);

      clearRecordingTimeout();

      // Cleanup any active recording on unmount
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      releaseStream();
    };
  }, [enabled, releaseStream, clearRecordingTimeout]);

  return { isRecording, error };
};

export default usePushToTalk;
